import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Clock, User as UserIcon } from 'lucide-react';
import { cardService } from '../services/cardService';
import { CardValidationResult } from '../types';
import Loading from '../components/common/Loading';
import { formatDateTime } from '../utils/formatters';

/**
 * Página pública aberta pelo QR Code do cartão virtual. Mostra ao parceiro se o
 * associado/dependente está ativo no momento da leitura.
 */
const CardValidation: React.FC = () => {
  const { hash = '', signature = '' } = useParams<{ hash: string; signature: string }>();
  const [result, setResult] = useState<CardValidationResult | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    cardService
      .validate(hash, signature)
      .then(setResult)
      .catch(() => setError('Não foi possível validar o cartão. Tente novamente.'));
  }, [hash, signature]);

  if (!result && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loading />
      </div>
    );
  }

  const expired = result?.reason === 'expirado';
  const valid = !!result?.valid;

  const theme = valid
    ? { bg: 'bg-[#0D8B55]', text: 'text-[#0D8B55]', Icon: CheckCircle2, title: 'Cartão Válido' }
    : expired
      ? { bg: 'bg-amber-500', text: 'text-amber-600', Icon: Clock, title: 'QR Code Expirado' }
      : { bg: 'bg-red-600', text: 'text-red-600', Icon: XCircle, title: 'Cartão Inválido' };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className={`${theme.bg} text-white text-center px-6 py-8`}>
          <theme.Icon className="w-16 h-16 mx-auto mb-3" />
          <h1 className="text-2xl font-extrabold">{theme.title}</h1>
          <p className="text-sm opacity-90 mt-1">{error || result?.message}</p>
        </div>

        {result?.nome && (
          <div className="p-6 space-y-5">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                {result.foto ? (
                  <img src={result.foto} alt={result.nome} className="h-full w-full object-cover" />
                ) : (
                  <UserIcon className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-lg font-bold text-gray-900 truncate">{result.nome}</p>
                {result.cpf && <p className="text-sm text-gray-500 font-mono">CPF: {result.cpf}</p>}
                <span
                  className={`inline-block mt-1 text-xs font-bold uppercase tracking-wider ${theme.text}`}
                >
                  {result.type === 'dependente' ? 'Dependente' : 'Titular'}
                </span>
              </div>
            </div>

            <dl className="divide-y divide-gray-100 border-t border-b border-gray-100 text-sm">
              {result.type === 'dependente' && (
                <>
                  <div className="flex justify-between py-2.5">
                    <dt className="text-gray-500">Titular</dt>
                    <dd className="font-semibold text-gray-900 text-right">{result.titular}</dd>
                  </div>
                  <div className="flex justify-between py-2.5">
                    <dt className="text-gray-500">Parentesco</dt>
                    <dd className="font-semibold text-gray-900 text-right">{result.parentesco}</dd>
                  </div>
                </>
              )}
              <div className="flex justify-between py-2.5">
                <dt className="text-gray-500">Plano</dt>
                <dd className="font-semibold text-gray-900 text-right">
                  {result.plano || 'Sem plano ativo'}
                </dd>
              </div>
              {result.validated_at && (
                <div className="flex justify-between py-2.5">
                  <dt className="text-gray-500">Validado em</dt>
                  <dd className="font-semibold text-gray-900 text-right">
                    {formatDateTime(result.validated_at)}
                  </dd>
                </div>
              )}
            </dl>

            <p className="text-xs text-gray-500 text-center leading-relaxed">
              Confira o nome com um documento de identificação com foto.
            </p>
          </div>
        )}

        <p className="text-xs text-gray-400 text-center pb-5 px-6">Cartão de Benefícios HMG</p>
      </div>
    </div>
  );
};

export default CardValidation;
